import React, { useState, useEffect, useMemo, useRef } from 'react';
import { databaseService } from '../../services/databaseService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend, BarChart, Bar } from 'recharts';
import styles from './Dashboard.module.css';

const WaterConsumptionReport = () => {
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [parkConsumption, setParkConsumption] = useState([]);
  const [parkConsumptionWithArea, setParkConsumptionWithArea] = useState([]); // Grafik için
  const [selectedParkForChart, setSelectedParkForChart] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sortBy, setSortBy] = useState('consumption');
  const [activeTab, setActiveTab] = useState('table'); // 'table', 'scatter', 'timeseries', 'area'
  const [searchTerm, setSearchTerm] = useState(''); // Park arama terimi
  const [timeseriesSearchTerm, setTimeseriesSearchTerm] = useState(''); // Zaman serisi park arama terimi
  const scatterChartRef = useRef(null);
  const timeSeriesChartRef = useRef(null);
  const areaScatterChartRef = useRef(null);
  const areaBarChartRef = useRef(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailConfigEnabled, setEmailConfigEnabled] = useState(false); // E-posta konfigürasyonu aktif mi?
  const [emailChartType, setEmailChartType] = useState(null); // 'scatter', 'timeseries', 'area-scatter', 'area-bar', 'report'
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Bölge listesini yükle
  useEffect(() => {
    loadRegions();
  }, []);

  // Bölge seçildiğinde park tüketim verilerini yükle
  useEffect(() => {
    if (selectedRegion) {
      loadParkConsumption(selectedRegion);
    } else {
      setParkConsumption([]);
      setParkConsumptionWithArea([]);
    }
  }, [selectedRegion]);

  // Park seçildiğinde zaman serisi verilerini yükle
  useEffect(() => {
    if (selectedParkForChart) {
      loadTimeSeriesData(selectedParkForChart);
    } else {
      setTimeSeriesData([]);
    }
  }, [selectedParkForChart]);

  const loadRegions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Reports.js'deki gibi bölgeleri çek - önce tüm parkları yükle ve bölgeleri çıkar
      const regionsList = [
        { id: 'all', name: 'Tüm Bölgeler' }
      ];
      
      try {
        // Parks tablosundan bölgeleri çek
        const structure = await databaseService.getTableStructure('parks', 'stage_z_z_park_ismi_txt');
        const columnNames = structure.map(col => col.column_name);
        
        // Bölge kolonunu bul
        let foundRegionColumn = columnNames.find(col => {
          const colName = col;
          return colName === 'İSTASYON İSMİ' ||
                 colName === 'İSTASYON_İSMİ' ||
                 colName.toLowerCase() === 'istasyon ismi' ||
                 colName.toLowerCase() === 'istasyon_ismi' ||
                 colName.includes('İSTASYON') ||
                 colName.includes('istasyon');
        });
        
        if (!foundRegionColumn) {
          foundRegionColumn = columnNames.find(col => {
            const colName = col.toLowerCase();
            return colName.includes('region') ||
                   colName.includes('district') ||
                   colName.includes('ilçe') ||
                   colName.includes('bölge');
          });
        }
        
        if (foundRegionColumn) {
          // Bölge kolonundan benzersiz bölgeleri çek
          const regionQuery = `SELECT DISTINCT "${foundRegionColumn}" as region FROM parks.stage_z_z_park_ismi_txt WHERE "${foundRegionColumn}" IS NOT NULL AND "${foundRegionColumn}" != '' ORDER BY "${foundRegionColumn}"`;
          const regionResult = await databaseService.executeQuery(regionQuery);
          
          if (regionResult.rows && regionResult.rows.length > 0) {
            regionResult.rows.forEach(row => {
              const regionName = row.region || row.region?.trim();
              if (regionName) {
                regionsList.push({
                  id: regionName,
                  name: regionName
                });
              }
            });
          }
        }
        
        // Eğer bölge kolonu bulunamadıysa, bilinen bölgeleri ekle
        if (regionsList.length === 1) {
          const knownRegions = ['Çankaya', 'Etimesgut', 'Keçiören', 'Mamak', 'Sincan', 'Yenimahalle', 'Akyurt', 'Beypazarı', 'Elmadağ', 'Gölbaşı', 'Haymana', 'Kazan', 'Nallıhan', 'Polatlı', 'Pursaklar'];
          knownRegions.forEach(region => {
            regionsList.push({ id: region, name: region });
          });
        }
      } catch (err) {
        console.warn('Bölgeler veritabanından çekilemedi, varsayılan bölgeler kullanılıyor:', err);
        // Varsayılan bölgeleri ekle
        const defaultRegions = ['Çankaya', 'Etimesgut', 'Keçiören', 'Mamak', 'Sincan', 'Yenimahalle'];
        defaultRegions.forEach(region => {
          if (!regionsList.find(r => r.id === region)) {
            regionsList.push({ id: region, name: region });
          }
        });
      }
      
      setRegions(regionsList);
      console.log('Yüklenen bölgeler:', regionsList);
    } catch (err) {
      console.error('Bölge listesi yükleme hatası:', err);
      setError(err.message || 'Bölge listesi yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const loadParkConsumption = async (regionId) => {
    try {
      setLoading(true);
      setError(null);
      
      const consumptionData = [];
      
      // water_billing.bill ve water_billing.park tablolarını birleştir
      try {
        // Bill tablosunun yapısını kontrol et
        const billStructure = await databaseService.getTableStructure('water_billing', 'bill');
        console.log('Bill tablosu kolonları:', billStructure.map(c => `${c.column_name} (${c.data_type})`));
        
        // Su tüketimi kolonu: consumption_m3
        const consumptionColumn = billStructure.find(col => col.column_name.toLowerCase() === 'consumption_m3');
        
        // Subscriber ID kolonu: subscriber_id (park'a ulaşmak için)
        const subscriberIdColumn = billStructure.find(col => col.column_name.toLowerCase() === 'subscriber_id');
        
        console.log('Bulunan kolonlar:');
        console.log('  - consumption_m3:', consumptionColumn ? 'BULUNDU' : 'BULUNAMADI');
        console.log('  - subscriber_id:', subscriberIdColumn ? 'BULUNDU' : 'BULUNAMADI');
        
        if (!consumptionColumn) {
          setError('Bill tablosunda consumption_m3 kolonu bulunamadı.');
          return;
        }
        
        if (!subscriberIdColumn) {
          setError('Bill tablosunda subscriber_id kolonu bulunamadı.');
          return;
        }
        
        // Subscriber tablosunun yapısını kontrol et (park_id'yi bulmak için)
        let subscriberParkIdColumn = null;
        try {
          const subscriberStructure = await databaseService.getTableStructure('water_billing', 'subscriber');
          console.log('Subscriber tablosu kolonları:', subscriberStructure.map(c => `${c.column_name} (${c.data_type})`));
          
          subscriberParkIdColumn = subscriberStructure.find(col => {
            const colName = col.column_name.toLowerCase();
            return (colName.includes('park') && colName.includes('id')) ||
                   colName === 'park_id';
          });
          
          console.log('Subscriber tablosunda park_id kolonu:', subscriberParkIdColumn ? subscriberParkIdColumn.column_name : 'BULUNAMADI');
        } catch (err) {
          console.warn('Subscriber tablosu okunamadı:', err);
        }
        
        // Bill ve Subscriber tablolarını birleştirerek park bazında tüketim topla
        // Bölge filtresi ekle (eğer seçildiyse ve subscriberParkIdColumn varsa)
        let regionJoin = '';
        let regionWhere = '';
        
        if (regionId && regionId !== 'all' && subscriberParkIdColumn) {
          // Park tablosundan bölge bilgisini almak için JOIN ekle
          try {
            const parkStructure = await databaseService.getTableStructure('parks', 'stage_z_z_park_ismi_txt');
            const parkIdColumn = parkStructure.find(col => col.column_name.toLowerCase() === 'id')?.column_name || 'id';
            const parkRegionColumn = parkStructure.find(col => {
              const colName = col.column_name;
              return colName === 'İSTASYON İSMİ' ||
                     colName === 'İSTASYON_İSMİ' ||
                     colName.toLowerCase().includes('istasyon') ||
                     colName.toLowerCase().includes('region') ||
                     colName.toLowerCase().includes('bölge');
            });
            
            if (parkRegionColumn && parkIdColumn) {
              // Bölge filtresi için park tablosuna JOIN ekle
              regionJoin = `
                JOIN parks.stage_z_z_park_ismi_txt p ON s.${subscriberParkIdColumn.column_name} = p."${parkIdColumn}"
              `;
              regionWhere = `AND p."${parkRegionColumn.column_name}" = '${regionId.replace(/'/g, "''")}'`;
            }
          } catch (err) {
            console.warn('Bölge filtresi eklenemedi:', err);
          }
        }
        
        let query;
        if (subscriberParkIdColumn) {
          // Subscriber üzerinden park'a ulaş
          query = `
            SELECT 
              s.${subscriberParkIdColumn.column_name} as park_id,
              SUM(b.${consumptionColumn.column_name}) as total_consumption
            FROM water_billing.bill b
            JOIN water_billing.subscriber s ON b.${subscriberIdColumn.column_name} = s.id
            ${regionJoin}
            WHERE s.${subscriberParkIdColumn.column_name} IS NOT NULL 
              AND b.${consumptionColumn.column_name} IS NOT NULL
              ${regionWhere}
            GROUP BY s.${subscriberParkIdColumn.column_name}
            LIMIT 1000
          `;
        } else {
          // Eğer subscriber'da park_id yoksa, subscriber_id'yi park_id olarak kullan (geçici çözüm)
          console.warn('Subscriber tablosunda park_id bulunamadı, subscriber_id kullanılıyor');
          query = `
            SELECT 
              b.${subscriberIdColumn.column_name} as park_id,
              SUM(b.${consumptionColumn.column_name}) as total_consumption
            FROM water_billing.bill b
            WHERE b.${subscriberIdColumn.column_name} IS NOT NULL 
              AND b.${consumptionColumn.column_name} IS NOT NULL
            GROUP BY b.${subscriberIdColumn.column_name}
            LIMIT 1000
          `;
        }
        
        console.log('Çalıştırılan sorgu:', query);
        
        const result = await databaseService.executeQuery(query);
        
        console.log('Sorgu sonucu:', result.rows?.length || 0, 'kayıt');
        if (result.rows && result.rows.length > 0) {
          console.log('İlk 3 kayıt örneği:', result.rows.slice(0, 3));
        }
        
        if (result.rows && result.rows.length > 0) {
          for (const row of result.rows) {
            const parkId = row.park_id;
            const consumption = parseFloat(row.total_consumption || 0);
            
            if (parkId && !isNaN(consumption) && consumption > 0) {
              consumptionData.push({
                parkId,
                consumption
              });
            }
          }
          console.log('Toplanan tüketim verisi:', consumptionData.length, 'park');
        } else {
          console.warn('Sorgu sonucu boş');
          setError('Bill tablosunda veri bulunamadı. Tablo boş olabilir veya subscriber-park ilişkisi kurulamıyor.');
        }
      } catch (err) {
        console.error('water_billing.bill tablosu okunamadı:', err);
        setError('Su tüketim verileri yüklenemedi: ' + err.message);
        return;
      }
      
      // Park isimlerini ve alanlarını al - toplu sorgu ile optimize et
      const aggregated = {};
      const consumptionWithArea = [];
      
      // Önce tüm park ID'lerini topla
      const parkIds = [...new Set(consumptionData.map(item => item.parkId))];
      
      // Park isimlerini toplu olarak çek (parks.stage_z_z_park_ismi_txt)
      const parkNamesMap = {};
      try {
        const parkNameStructure = await databaseService.getTableStructure('parks', 'stage_z_z_park_ismi_txt');
        const columnNames = parkNameStructure.map(col => col.column_name);
        console.log('Park tablosu kolonları:', columnNames);
        
        // ID kolonunu bul
        const idColumn = parkNameStructure.find(col => 
          col.column_name.toLowerCase() === 'id' ||
          col.column_name.toLowerCase() === 'park_id'
        )?.column_name || columnNames[0];
        
        // İsim kolonunu bul - daha kapsamlı arama
        let nameColumn = parkNameStructure.find(col => {
          const colName = col.column_name.toLowerCase();
          return colName.includes('park') && (colName.includes('name') || colName.includes('ismi')) ||
                 colName === 'park_name' ||
                 colName === 'park_ismi' ||
                 colName === 'name' ||
                 colName === 'ismi' ||
                 colName.includes('mahal') ||
                 colName.includes('adı');
        })?.column_name;
        
        // Eğer bulunamadıysa, "park" içeren ilk kolonu dene
        if (!nameColumn) {
          nameColumn = columnNames.find(col => 
            col.toLowerCase().includes('park') && 
            !col.toLowerCase().includes('id')
          );
        }
        
        // Hala bulunamadıysa, ikinci kolonu kullan
        if (!nameColumn) {
          nameColumn = columnNames[1] || columnNames[0];
        }
        
        console.log('Park isim kolonu:', nameColumn, 'ID kolonu:', idColumn);
        
        if (nameColumn && parkIds.length > 0) {
          const parkIdsStr = parkIds.join(',');
          // Tırnak işaretleri ile korumalı sorgu
          const parkQuery = `SELECT "${idColumn}" as id, "${nameColumn}" as name FROM parks.stage_z_z_park_ismi_txt WHERE "${idColumn}" IN (${parkIdsStr})`;
          console.log('Park isim sorgusu:', parkQuery);
          const parkResult = await databaseService.executeQuery(parkQuery);
          
          if (parkResult.rows && parkResult.rows.length > 0) {
            console.log('Park isimleri bulundu:', parkResult.rows.length, 'park');
            parkResult.rows.forEach(row => {
              const parkName = row.name || row[nameColumn] || null;
              if (parkName && parkName.trim() !== '' && parkName !== 'null') {
                parkNamesMap[row.id] = parkName.trim();
              } else {
                parkNamesMap[row.id] = `Park ${row.id}`;
              }
            });
            console.log('İlk 5 park ismi örneği:', Object.entries(parkNamesMap).slice(0, 5));
          } else {
            console.warn('Park isim sorgusu sonuç döndürmedi');
          }
        }
      } catch (err) {
        console.warn('Park isimleri toplu çekilemedi:', err);
        // Alternatif: Tüm parkları çek ve eşleştir
        try {
          const allParksQuery = `SELECT id, name FROM parks.stage_z_z_park_ismi_txt LIMIT 10000`;
          const allParksResult = await databaseService.executeQuery(allParksQuery);
          if (allParksResult.rows) {
            allParksResult.rows.forEach(row => {
              if (parkIds.includes(row.id) && row.name) {
                parkNamesMap[row.id] = row.name;
              }
            });
          }
        } catch (err2) {
          console.warn('Alternatif park isim sorgusu da başarısız:', err2);
        }
      }
      
      // Park alanlarını toplu olarak çek - birden fazla kaynaktan
      const parkAreasMap = {};
      
      // 1. water_billing.park_area tablosunu dene
      try {
        const parkAreaStructure = await databaseService.getTableStructure('water_billing', 'park_area');
        const parkAreaIdCol = parkAreaStructure.find(col => 
          col.column_name.toLowerCase().includes('park') && 
          col.column_name.toLowerCase().includes('id')
        )?.column_name;
        const totalAreaCol = parkAreaStructure.find(col => 
          col.column_name.toLowerCase().includes('total') && 
          col.column_name.toLowerCase().includes('area')
        )?.column_name || parkAreaStructure.find(col => 
          col.column_name.toLowerCase().includes('area')
        )?.column_name;
        
        if (parkAreaIdCol && totalAreaCol && parkIds.length > 0) {
          const parkIdsStr = parkIds.join(',');
          const areaQuery = `SELECT "${parkAreaIdCol}" as park_id, "${totalAreaCol}" as total_area FROM water_billing.park_area WHERE "${parkAreaIdCol}" IN (${parkIdsStr})`;
          const areaResult = await databaseService.executeQuery(areaQuery);
          
          if (areaResult.rows && areaResult.rows.length > 0) {
            areaResult.rows.forEach(row => {
              const area = parseFloat(row.total_area) || 0;
              if (area > 0) {
                parkAreasMap[row.park_id] = area;
              }
            });
            console.log('water_billing.park_area\'dan', Object.keys(parkAreasMap).length, 'park alanı bulundu');
          }
        }
      } catch (err) {
        console.warn('water_billing.park_area bulunamadı:', err);
      }
      
      // 2. public.park_area tablosunu dene
      if (Object.keys(parkAreasMap).length === 0) {
        try {
          const publicAreaStructure = await databaseService.getTableStructure('public', 'park_area');
          const publicAreaIdCol = publicAreaStructure.find(col => 
            col.column_name.toLowerCase().includes('park') && 
            col.column_name.toLowerCase().includes('id')
          )?.column_name || 'id';
          const publicTotalAreaCol = publicAreaStructure.find(col => 
            col.column_name.toLowerCase().includes('total') && 
            col.column_name.toLowerCase().includes('area')
          )?.column_name || publicAreaStructure.find(col => 
            col.column_name.toLowerCase().includes('area')
          )?.column_name;
          
          if (publicTotalAreaCol && parkIds.length > 0) {
            const parkIdsStr = parkIds.join(',');
            const areaQuery = `SELECT "${publicAreaIdCol}" as park_id, "${publicTotalAreaCol}" as total_area FROM public.park_area WHERE "${publicAreaIdCol}" IN (${parkIdsStr})`;
            const areaResult = await databaseService.executeQuery(areaQuery);
            
            if (areaResult.rows && areaResult.rows.length > 0) {
              areaResult.rows.forEach(row => {
                const area = parseFloat(row.total_area) || 0;
                if (area > 0) {
                  parkAreasMap[row.park_id] = area;
                }
              });
              console.log('public.park_area\'dan', Object.keys(parkAreasMap).length, 'park alanı bulundu');
            }
          }
        } catch (err2) {
          console.warn('public.park_area da bulunamadı:', err2);
        }
      }
      
      // 3. parks.stage_z_z_park_ismi_txt tablosundan alan bilgisini dene (ÇİM, SERT, KALDIRIM toplamı)
      if (Object.keys(parkAreasMap).length === 0) {
        try {
          const parkStructure = await databaseService.getTableStructure('parks', 'stage_z_z_park_ismi_txt');
          const idColumn = parkStructure.find(col => col.column_name.toLowerCase() === 'id')?.column_name || 'id';
          
          // Alan bilgisi içeren kolonları bul
          const cimColumn = parkStructure.find(col => 
            col.column_name.includes('ÇİM') || 
            col.column_name.toLowerCase().includes('cim') ||
            col.column_name.toLowerCase().includes('yesil')
          )?.column_name;
          
          const sertColumn = parkStructure.find(col => 
            col.column_name.includes('SERT') || 
            col.column_name.toLowerCase().includes('sert')
          )?.column_name;
          
          const kaldirimColumn = parkStructure.find(col => 
            col.column_name.includes('KALDIRIM') || 
            col.column_name.toLowerCase().includes('kaldirim')
          )?.column_name;
          
          if ((cimColumn || sertColumn || kaldirimColumn) && parkIds.length > 0) {
            const parkIdsStr = parkIds.join(',');
            let areaQuery = `SELECT "${idColumn}" as id`;
            
            if (cimColumn) areaQuery += `, "${cimColumn}" as cim`;
            if (sertColumn) areaQuery += `, "${sertColumn}" as sert`;
            if (kaldirimColumn) areaQuery += `, "${kaldirimColumn}" as kaldirim`;
            
            areaQuery += ` FROM parks.stage_z_z_park_ismi_txt WHERE "${idColumn}" IN (${parkIdsStr})`;
            
            const areaResult = await databaseService.executeQuery(areaQuery);
            
            if (areaResult.rows && areaResult.rows.length > 0) {
              areaResult.rows.forEach(row => {
                const cim = parseFloat(row.cim || 0) || 0;
                const sert = parseFloat(row.sert || 0) || 0;
                const kaldirim = parseFloat(row.kaldirim || 0) || 0;
                const totalArea = cim + sert + kaldirim;
                
                if (totalArea > 0) {
                  parkAreasMap[row.id] = totalArea;
                }
              });
              console.log('parks.stage_z_z_park_ismi_txt\'den (ÇİM+SERT+KALDIRIM)', Object.keys(parkAreasMap).length, 'park alanı bulundu');
            }
          }
        } catch (err3) {
          console.warn('parks.stage_z_z_park_ismi_txt\'den alan bilgisi çekilemedi:', err3);
        }
      }
      
      // 4. Diğer olası tabloları kontrol et
      if (Object.keys(parkAreasMap).length === 0) {
        try {
          const schemas = await databaseService.listSchemas();
          for (const schema of schemas) {
            try {
              const tables = await databaseService.listTables(schema);
              const areaTables = tables.filter(table => 
                table.toLowerCase().includes('area') ||
                table.toLowerCase().includes('alan')
              );
              
              for (const table of areaTables) {
                try {
                  const structure = await databaseService.getTableStructure(schema, table);
                  const idCol = structure.find(col => 
                    col.column_name.toLowerCase().includes('park') && 
                    col.column_name.toLowerCase().includes('id')
                  )?.column_name;
                  const areaCol = structure.find(col => 
                    col.column_name.toLowerCase().includes('area') ||
                    col.column_name.toLowerCase().includes('alan')
                  )?.column_name;
                  
                  if (idCol && areaCol && parkIds.length > 0) {
                    const parkIdsStr = parkIds.join(',');
                    const areaQuery = `SELECT "${idCol}" as park_id, "${areaCol}" as total_area FROM ${schema}.${table} WHERE "${idCol}" IN (${parkIdsStr}) LIMIT 1000`;
                    const areaResult = await databaseService.executeQuery(areaQuery);
                    
                    if (areaResult.rows && areaResult.rows.length > 0) {
                      areaResult.rows.forEach(row => {
                        const area = parseFloat(row.total_area) || 0;
                        if (area > 0 && !parkAreasMap[row.park_id]) {
                          parkAreasMap[row.park_id] = area;
                        }
                      });
                      console.log(`${schema}.${table}'dan`, Object.keys(parkAreasMap).length, 'park alanı bulundu');
                      break; // İlk başarılı tabloyu kullan
                    }
                  }
                } catch (err) {
                  continue;
                }
              }
            } catch (err) {
              continue;
            }
          }
        } catch (err4) {
          console.warn('Diğer tablolardan alan bilgisi aranırken hata:', err4);
        }
      }
      
      console.log('Toplam', Object.keys(parkAreasMap).length, 'park için alan bilgisi bulundu');
      
      // Verileri birleştir
      for (const item of consumptionData) {
        if (!aggregated[item.parkId]) {
          const parkName = parkNamesMap[item.parkId] || `Park ${item.parkId}`;
          const parkArea = parkAreasMap[item.parkId] || 0;
          
          aggregated[item.parkId] = {
            parkId: item.parkId,
            parkName,
            consumption: item.consumption,
            area: parkArea
          };
          
          // Grafik için veri - alan bilgisi olmasa bile ekle (0 alan ile)
          consumptionWithArea.push({
            parkId: item.parkId,
            parkName,
            area: parkArea || 0, // Alan bilgisi yoksa 0 kullan
            consumption: item.consumption
          });
        } else {
          aggregated[item.parkId].consumption += item.consumption;
          // Grafik verisini de güncelle
          const existingIndex = consumptionWithArea.findIndex(p => p.parkId === item.parkId);
          if (existingIndex >= 0) {
            consumptionWithArea[existingIndex].consumption += item.consumption;
          }
        }
      }
      
      // Sırala
      const sorted = Object.values(aggregated).sort((a, b) => {
        if (sortBy === 'consumption') {
          return b.consumption - a.consumption;
        } else {
          return a.parkName.localeCompare(b.parkName);
        }
      });
      
      setParkConsumption(sorted);
      setParkConsumptionWithArea(consumptionWithArea);
      
      if (sorted.length === 0) {
        setError('Su tüketim verisi bulunamadı. Veritabanında water_billing.bill tablosunu kontrol edin.');
      }
    } catch (err) {
      console.error('Park tüketim verisi yükleme hatası:', err);
      setError(err.message || 'Park tüketim verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const loadTimeSeriesData = async (parkId) => {
    try {
      setLoading(true);
      
      // billing_period ve bill tablolarını birleştir
      const billStructure = await databaseService.getTableStructure('water_billing', 'bill');
      const periodStructure = await databaseService.getTableStructure('water_billing', 'billing_period');
      
      console.log('Billing Period tablosu kolonları:', periodStructure.map(c => `${c.column_name} (${c.data_type})`));
      
      const subscriberIdColumn = billStructure.find(col => col.column_name.toLowerCase() === 'subscriber_id');
      const periodIdColumn = billStructure.find(col => col.column_name.toLowerCase() === 'billing_period_id');
      const consumptionColumn = billStructure.find(col => col.column_name.toLowerCase() === 'consumption_m3');
      
      // Subscriber tablosundan park_id'yi bul
      let subscriberParkIdColumn = null;
      try {
        const subscriberStructure = await databaseService.getTableStructure('water_billing', 'subscriber');
        subscriberParkIdColumn = subscriberStructure.find(col => {
          const colName = col.column_name.toLowerCase();
          return (colName.includes('park') && colName.includes('id')) ||
                 colName === 'park_id';
        });
      } catch (err) {
        console.warn('Subscriber tablosu okunamadı:', err);
      }
      
      // Billing period tablosundan tarih kolonunu bul
      const periodDateColumn = periodStructure.find(col => {
        const colName = col.column_name.toLowerCase();
        return colName.includes('date') || 
               colName.includes('period') ||
               colName.includes('month') ||
               colName.includes('year') ||
               colName.includes('start') ||
               colName.includes('end');
      }) || periodStructure.find(col => col.data_type?.toLowerCase().includes('date')) || periodStructure[1];
      
      if (!subscriberIdColumn || !periodIdColumn || !consumptionColumn || !periodDateColumn) {
        console.warn('Gerekli kolonlar bulunamadı');
        setTimeSeriesData([]);
        return;
      }
      
      let query;
      if (subscriberParkIdColumn) {
        // Subscriber üzerinden park'a ulaş
        query = `
          SELECT 
            bp.${periodDateColumn.column_name} as period_date,
            SUM(b.${consumptionColumn.column_name}) as consumption
          FROM water_billing.bill b
          JOIN water_billing.subscriber s ON b.${subscriberIdColumn.column_name} = s.id
          JOIN water_billing.billing_period bp ON b.${periodIdColumn.column_name} = bp.id
          WHERE s.${subscriberParkIdColumn.column_name} = ${parkId}
            AND b.${consumptionColumn.column_name} IS NOT NULL
          GROUP BY bp.${periodDateColumn.column_name}
          ORDER BY bp.${periodDateColumn.column_name}
          LIMIT 100
        `;
      } else {
        // Subscriber ID'yi park ID olarak kullan
        query = `
          SELECT 
            bp.${periodDateColumn.column_name} as period_date,
            SUM(b.${consumptionColumn.column_name}) as consumption
          FROM water_billing.bill b
          JOIN water_billing.billing_period bp ON b.${periodIdColumn.column_name} = bp.id
          WHERE b.${subscriberIdColumn.column_name} = ${parkId}
            AND b.${consumptionColumn.column_name} IS NOT NULL
          GROUP BY bp.${periodDateColumn.column_name}
          ORDER BY bp.${periodDateColumn.column_name}
          LIMIT 100
        `;
      }
      
      console.log('Zaman serisi sorgusu:', query);
      
      const result = await databaseService.executeQuery(query);
      
        if (result.rows && result.rows.length > 0) {
          console.log('İlk 3 ham veri örneği:', result.rows.slice(0, 3));
          
          const timeData = result.rows.map((row, index) => {
            let periodLabel = 'Bilinmeyen';
            let fullDate = null;
            let year = null;
            let month = null;
            let day = null;
            
            if (row.period_date) {
              try {
                let date = null;
                const dateValue = row.period_date;
                
                // YYYYMM formatını kontrol et (örn: 201709, 201710, 201805)
                const dateStr = String(dateValue).trim();
                
                // YYYYMM formatı (6 haneli sayı: YYYYMM)
                if (/^\d{6}$/.test(dateStr)) {
                  year = parseInt(dateStr.substring(0, 4), 10);
                  month = parseInt(dateStr.substring(4, 6), 10);
                  day = 1; // Ayın ilk günü
                  
                  if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
                    date = new Date(year, month - 1, day);
                  }
                }
                // YYYY-MM-DD veya YYYY-MM-DD HH:MM:SS formatı
                else if (typeof dateValue === 'string' && dateStr.includes('-')) {
                  const parts = dateStr.split(/[-/]/);
                  if (parts.length >= 3) {
                    year = parseInt(parts[0], 10);
                    month = parseInt(parts[1], 10);
                    day = parseInt(parts[2].split(' ')[0], 10); // Zaman varsa sadece günü al
                    
                    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                      date = new Date(year, month - 1, day);
                    }
                  }
                }
                // Date objesi
                else if (dateValue instanceof Date) {
                  date = dateValue;
                }
                // Timestamp veya diğer formatlar
                else {
                  date = new Date(dateValue);
                }
                
                if (date && !isNaN(date.getTime())) {
                  // Eğer yukarıdaki parse'lardan year/month/day alınmadıysa, date'den al
                  if (!year || !month) {
                    year = date.getFullYear();
                    month = date.getMonth() + 1; // 1-12
                    day = date.getDate();
                  }
                  
                  // Format: "1 Eylül 2017" (ayın ilk günü)
                  const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                                     'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
                  periodLabel = `${day} ${monthNames[month - 1]} ${year}`;
                  fullDate = date;
                  
                  // Debug: İlk birkaç tarih için log
                  if (index < 5) {
                    console.log(`Tarih parse: "${dateValue}" -> ${periodLabel}`, { year, month, day });
                  }
                } else {
                  periodLabel = String(row.period_date);
                  console.warn(`Tarih parse edilemedi: "${dateValue}"`);
                }
              } catch (err) {
                periodLabel = String(row.period_date);
                console.warn(`Tarih parse hatası: "${row.period_date}"`, err);
              }
            }
            
            return {
              period: periodLabel,
              consumption: parseFloat(row.consumption || 0),
              date: fullDate,
              year: year,
              month: month,
              day: day,
              timestamp: fullDate ? fullDate.getTime() : 0
            };
          }).sort((a, b) => {
            // Tarihe göre sırala
            if (a.timestamp && b.timestamp) {
              return a.timestamp - b.timestamp;
            }
            return 0;
          });
          
          console.log('Zaman serisi verisi:', timeData.length, 'dönem');
          console.log('İlk 5 parse edilmiş tarih:', timeData.slice(0, 5).map(d => ({ period: d.period, year: d.year, month: d.month, day: d.day })));
          
          setTimeSeriesData(timeData);
      } else {
        setTimeSeriesData([]);
        console.warn('Zaman serisi verisi bulunamadı');
      }
    } catch (err) {
      console.error('Zaman serisi verisi yükleme hatası:', err);
      setTimeSeriesData([]);
    } finally {
      setLoading(false);
    }
  };

  const generateChartPDF = async (chartRef, chartTitle, fileName, returnAsBase64 = false) => {
    if (!chartRef || !chartRef.current) {
      setError('Grafik bulunamadı. Lütfen grafiğin yüklendiğinden emin olun.');
      return null;
    }

    try {
      setGenerating(true);
      
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 1.2, // Daha düşük scale ile daha küçük dosya
        logging: false,
        useCORS: true,
        allowTaint: false,
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.7); // JPEG ve daha düşük kalite ile daha küçük dosya
      const imgWidth = 190; // A4 width in mm - margin bırak
      const pageHeight = 280; // A4 height in mm - margin bırak
      const pageWidth = 210;
      const margin = 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      const doc = new jsPDF('p', 'mm', 'a4');
      let position = margin + 15; // Başlık için yer bırak
      
      // Başlık - daha küçük font
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      const titleLines = doc.splitTextToSize(chartTitle, imgWidth);
      doc.text(titleLines, pageWidth / 2, margin + 10, { align: 'center', maxWidth: imgWidth });
      
      // Grafik ekle
      doc.addImage(imgData, 'JPEG', margin, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - position);
      
      // Eğer grafik sayfaya sığmazsa yeni sayfa ekle
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(imgData, 'JPEG', margin, margin, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      if (returnAsBase64) {
        // PDF'i arraybuffer olarak al ve base64'e çevir (daha güvenilir)
        const pdfArrayBuffer = doc.output('arraybuffer');
        // ArrayBuffer'ı base64'e çevir
        const uint8Array = new Uint8Array(pdfArrayBuffer);
        let binaryString = '';
        for (let i = 0; i < uint8Array.length; i++) {
          binaryString += String.fromCharCode(uint8Array[i]);
        }
        const cleanBase64 = btoa(binaryString);
        setGenerating(false);
        return { pdfBase64: cleanBase64, fileName };
      } else {
        doc.save(fileName);
        setGenerating(false);
        return null;
      }
    } catch (err) {
      console.error('Grafik PDF oluşturma hatası:', err);
      setError('Grafik PDF\'i oluşturulurken bir hata oluştu: ' + err.message);
      setGenerating(false);
      return null;
    }
  };

  const sendChartEmail = async (chartRef, chartTitle, fileName) => {
    if (!emailTo || !emailTo.includes('@')) {
      setError('Geçerli bir e-posta adresi girin.');
      return;
    }

    try {
      setSendingEmail(true);
      setError(null);

      // PDF'i base64 olarak oluştur
      const pdfData = await generateChartPDF(chartRef, chartTitle, fileName, true);
      if (!pdfData) {
        setError('Grafik PDF\'i oluşturulamadı.');
        setSendingEmail(false);
        return;
      }

      // Backend API'ye gönder
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      if (!API_BASE_URL) {
        setError('Backend API endpoint tanımlı değil.');
        setSendingEmail(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: emailTo.split(',').map(e => e.trim()),
          subject: emailSubject || chartTitle,
          body: emailBody || `${chartTitle} grafiği ektedir.`,
          pdfBase64: pdfData.pdfBase64,
          pdfFileName: pdfData.fileName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'E-posta gönderilemedi' }));
        throw new Error(errorData.message || 'E-posta gönderilemedi');
      }

      const result = await response.json();
      
      if (result.success) {
        // Başarı bildirimi - toast notification
        const recipientCount = emailTo.split(',').length;
        const recipientText = recipientCount === 1 
          ? `E-posta başarıyla gönderildi!` 
          : `${recipientCount} alıcıya e-posta başarıyla gönderildi!`;
        
        setSuccessMessage(`${recipientText}\n\nPDF: ${pdfData.fileName}\nAlici: ${emailTo}`);
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 5000);
        
        setShowEmailModal(false);
        setEmailTo('');
        setEmailSubject('');
        setEmailBody('');
        setEmailChartType(null);
      } else {
        throw new Error(result.message || 'E-posta gönderilemedi');
      }
    } catch (err) {
      console.error('E-posta gönderme hatası:', err);
      setError('E-posta gönderilemedi: ' + err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const generatePDF = async (returnAsBase64 = false) => {
    if (!selectedRegion || parkConsumption.length === 0) {
      setError('Rapor oluşturmak için bölge seçin ve verileri yükleyin.');
      return null;
    }

    try {
      setGenerating(true);
      
      const region = regions.find(r => r.id === selectedRegion);
      const regionName = region?.name || 'Tüm Bölgeler';
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 14;
      
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text('Su Tüketimi Raporu', pageWidth / 2, 20, { align: 'center' });
      
      doc.setFontSize(14);
      doc.setFont(undefined, 'normal');
      doc.text(`Bölge: ${regionName}`, pageWidth / 2, 30, { align: 'center' });
      doc.text(`Rapor Tarihi: ${new Date().toLocaleDateString('tr-TR')}`, pageWidth / 2, 37, { align: 'center' });
      
      let yPosition = 50;
      
      // Park Su Tüketimi Tablosu
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Park Su Tüketimi (m³)', margin, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Sıra', margin, yPosition);
      doc.text('Park Adı', margin + 20, yPosition);
      doc.text('Yeşil Alan (m²)', margin + 100, yPosition);
      doc.text('Su Tüketimi (m³)', margin + 150, yPosition);
      yPosition += 8;
      
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 5;
      
      doc.setFont(undefined, 'normal');
      parkConsumption.forEach((item, index) => {
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.text((index + 1).toString(), margin, yPosition);
        
        const parkNameText = doc.splitTextToSize(item.parkName || `Park ${item.parkId}`, 70);
        doc.text(parkNameText, margin + 20, yPosition);
        const nameHeight = parkNameText.length * 5;
        
        const areaText = item.area > 0 ? item.area.toFixed(2) : 'Bilinmiyor';
        doc.text(areaText, margin + 100, yPosition);
        
        doc.text(item.consumption.toFixed(2), margin + 150, yPosition);
        yPosition += Math.max(nameHeight, 5);
      });
      
      const total = parkConsumption.reduce((sum, item) => sum + item.consumption, 0);
      const totalArea = parkConsumption.reduce((sum, item) => sum + (item.area || 0), 0);
      yPosition += 5;
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 5;
      doc.setFont(undefined, 'bold');
      doc.text('Toplam:', margin + 20, yPosition);
      doc.text(totalArea > 0 ? totalArea.toFixed(2) + ' m²' : '--', margin + 100, yPosition);
      doc.text(total.toFixed(2) + ' m³', margin + 150, yPosition);
      
      // Zaman Serisi Grafiği (eğer seçili park varsa)
      if (selectedParkForChart && timeSeriesData.length > 0) {
        yPosition += 15;
        if (yPosition > pageHeight - 50) {
          doc.addPage();
          yPosition = 20;
        }
        
        const selectedPark = parkConsumption.find(p => p.parkId === selectedParkForChart);
        const parkName = selectedPark?.parkName || `Park ${selectedParkForChart}`;
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`Zaman Serisi Analizi: ${parkName}`, margin, yPosition);
        yPosition += 10;
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text('Dönem', margin, yPosition);
        doc.text('Tüketim (m³)', margin + 80, yPosition);
        yPosition += 8;
        
        doc.setLineWidth(0.3);
        doc.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += 5;
        
        // Son 12 dönemi göster
        const recentData = timeSeriesData.slice(-12);
        recentData.forEach((item) => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }
          
          doc.text(item.period || 'Bilinmeyen', margin, yPosition);
          doc.text(item.consumption.toFixed(2), margin + 80, yPosition);
          yPosition += 5;
        });
      }
      
      const fileName = `Su_Tuketimi_Raporu_${regionName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30)}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      if (returnAsBase64) {
        // PDF'i arraybuffer olarak al ve base64'e çevir (daha güvenilir)
        const pdfArrayBuffer = doc.output('arraybuffer');
        // ArrayBuffer'ı base64'e çevir
        const uint8Array = new Uint8Array(pdfArrayBuffer);
        let binaryString = '';
        for (let i = 0; i < uint8Array.length; i++) {
          binaryString += String.fromCharCode(uint8Array[i]);
        }
        const cleanBase64 = btoa(binaryString);
        setGenerating(false);
        return { pdfBase64: cleanBase64, fileName };
      } else {
        // Normal indirme
        doc.save(fileName);
        setGenerating(false);
        return null;
      }
    } catch (err) {
      console.error('PDF oluşturma hatası:', err);
      setError('PDF oluşturulurken bir hata oluştu: ' + err.message);
      setGenerating(false);
      return null;
    }
  };

  const sendEmailWithPDF = async () => {
    if (!emailTo || !emailTo.includes('@')) {
      setError('Geçerli bir e-posta adresi girin.');
      return;
    }

    try {
      setSendingEmail(true);
      setError(null);

      // PDF'i base64 olarak oluştur
      const pdfData = await generatePDF(true);
      if (!pdfData) {
        setError('PDF oluşturulamadı.');
        setSendingEmail(false);
        return;
      }

      // Backend API'ye gönder
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      if (!API_BASE_URL) {
        setError('Backend API endpoint tanımlı değil.');
        setSendingEmail(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: emailTo.split(',').map(e => e.trim()), // Virgülle ayrılmış e-postalar
          subject: emailSubject || `Su Tüketimi Raporu - ${regions.find(r => r.id === selectedRegion)?.name || 'Rapor'}`,
          body: emailBody || 'Su tüketimi raporu ektedir.',
          pdfBase64: pdfData.pdfBase64,
          pdfFileName: pdfData.fileName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'E-posta gönderilemedi' }));
        throw new Error(errorData.message || 'E-posta gönderilemedi');
      }

      const result = await response.json();
      
      if (result.success) {
        // Başarı bildirimi - toast notification
        const recipientCount = emailTo.split(',').length;
        const recipientText = recipientCount === 1 
          ? `E-posta başarıyla gönderildi!` 
          : `${recipientCount} alıcıya e-posta başarıyla gönderildi!`;
        
        setSuccessMessage(`${recipientText}\n\nPDF: ${pdfData.fileName}\nAlici: ${emailTo}`);
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 5000);
        
        setShowEmailModal(false);
        setEmailTo('');
        setEmailSubject('');
        setEmailBody('');
        setEmailChartType(null);
      } else {
        throw new Error(result.message || 'E-posta gönderilemedi');
      }
    } catch (err) {
      console.error('E-posta gönderme hatası:', err);
      setError('E-posta gönderilemedi: ' + err.message);
    } finally {
      setSendingEmail(false);
    }
  };

  // E-posta konfigürasyonunu kontrol et
  useEffect(() => {
    const checkEmailConfig = async () => {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
        if (API_BASE_URL) {
          // Backend'den e-posta konfigürasyonunu kontrol et (opsiyonel endpoint)
          // Şimdilik environment variable'dan kontrol edelim
          setEmailConfigEnabled(true); // Varsayılan olarak aktif
        }
      } catch (err) {
        console.warn('E-posta konfigürasyonu kontrol edilemedi:', err);
        setEmailConfigEnabled(false);
      }
    };
    checkEmailConfig();
  }, []);

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    const sorted = [...parkConsumption].sort((a, b) => {
      if (newSort === 'consumption') {
        return b.consumption - a.consumption;
      } else {
        return a.parkName.localeCompare(b.parkName);
      }
    });
    setParkConsumption(sorted);
  };
  
  // Filtrelenmiş parklar (arama terimine göre)
  const filteredParkConsumption = useMemo(() => {
    if (!searchTerm.trim()) return parkConsumption;
    const searchLower = searchTerm.toLowerCase();
    return parkConsumption.filter(park => {
      const parkName = (park.parkName || `Park ${park.parkId}`).toLowerCase();
      return parkName.includes(searchLower) ||
             String(park.parkId).includes(searchTerm);
    });
  }, [parkConsumption, searchTerm]);

  return (
    <div className={styles.reportsContainer}>
      <div className={styles.reportsHeader}>
        <h2>Su Tüketimi Raporları</h2>
        <p className={styles.reportsSubtitle}>
          Bölge seçin ve parkların su tüketim verilerini görüntüleyin
        </p>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button 
            onClick={() => setError(null)} 
            style={{ marginLeft: '12px', background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      )}

      <div className={styles.reportsContent}>
        <div className={styles.reportSelector}>
          <div className={styles.reportSelectorRow}>
            <label htmlFor="regionSelect">Bölge Seçin:</label>
            <select
              id="regionSelect"
              value={selectedRegion || ''}
              onChange={(e) => setSelectedRegion(e.target.value || null)}
              disabled={loading || regions.length === 0}
              className={styles.parkSelect}
            >
              <option value="">-- Bölge Seçin --</option>
              {regions.map(region => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </select>
          </div>
          
          {selectedRegion && (
            <>
              <div className={styles.selectedParkInfo}>
                <strong>Seçilen Bölge:</strong> {regions.find(r => r.id === selectedRegion)?.name || 'Bilinmeyen'}
              </div>
              
              {/* Park Arama */}
              <div className={styles.reportSelectorRow}>
                <label>Park Ara:</label>
                <input
                  type="text"
                  placeholder="Park adı veya ID ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              
              <div className={styles.reportSelectorRow}>
                <label>Sıralama:</label>
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className={styles.parkSelect}
                  style={{ maxWidth: '200px' }}
                >
                  <option value="consumption">Su Tüketimine Göre (Yüksek → Düşük)</option>
                  <option value="name">Park Adına Göre (A → Z)</option>
                </select>
                <div style={{ flex: 1 }}></div>
                <button
                  onClick={generatePDF}
                  disabled={generating || parkConsumption.length === 0}
                  className={styles.generatePdfButton}
                >
                  {generating ? 'PDF Oluşturuluyor...' : 'PDF Indir'}
                </button>
                {emailConfigEnabled && (
                  <button
                    onClick={() => {
                      setEmailChartType('report');
                      setShowEmailModal(true);
                    }}
                    disabled={generating || parkConsumption.length === 0}
                    className={styles.generatePdfButton}
                    style={{ marginLeft: '10px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                  >
                    E-posta ile Gönder
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {selectedRegion && parkConsumption.length > 0 && (
          <div className={styles.chartTabs}>
            <button
              className={`${styles.chartTab} ${activeTab === 'table' ? styles.active : ''}`}
              onClick={() => setActiveTab('table')}
            >
Tablo
            </button>
            <button
              className={`${styles.chartTab} ${activeTab === 'scatter' ? styles.active : ''}`}
              onClick={() => setActiveTab('scatter')}
            >
              Park Boyutu vs Tuketim
            </button>
            <button
              className={`${styles.chartTab} ${activeTab === 'timeseries' ? styles.active : ''}`}
              onClick={() => setActiveTab('timeseries')}
            >
              Zaman Serisi
            </button>
            <button
              className={`${styles.chartTab} ${activeTab === 'area' ? styles.active : ''}`}
              onClick={() => setActiveTab('area')}
            >
              Yesil Alan Analizi
            </button>
          </div>
        )}

        {loading && (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>{selectedRegion ? 'Su tüketim verileri yükleniyor...' : 'Bölge listesi yükleniyor...'}</p>
          </div>
        )}

        {selectedRegion && parkConsumption.length > 0 && activeTab === 'table' && (
          <div className={styles.parkDataPreview}>
            <h3>Park Su Tüketimi</h3>
            <p className={styles.dataInfo}>
              Toplam {parkConsumption.length} park bulundu ({filteredParkConsumption.length} filtrelenmiş). Toplam tüketim: {parkConsumption.reduce((sum, item) => sum + item.consumption, 0).toFixed(2)} m³
            </p>
            
            <div className={styles.consumptionTable}>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Sıra</th>
                    <th>Park Adı</th>
                    <th>Yeşil Alan (m²)</th>
                    <th>Su Tüketimi (m³)</th>
                    <th>Tüketim/Alan (m³/m²)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParkConsumption.map((item, index) => {
                    const consumptionPerArea = item.area > 0 ? (item.consumption / item.area).toFixed(4) : '--';
                    return (
                      <tr key={item.parkId}>
                        <td>{index + 1}</td>
                        <td>{item.parkName || `Park ${item.parkId}`}</td>
                        <td>{item.area > 0 ? item.area.toFixed(2) : 'Bilinmiyor'}</td>
                        <td>{item.consumption.toFixed(2)}</td>
                        <td>{consumptionPerArea}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {selectedRegion && parkConsumptionWithArea.length > 0 && activeTab === 'scatter' && (
          <div className={styles.chartContainer}>
            <h3>Park Boyutu vs Su Tüketimi</h3>
            <p className={styles.dataInfo}>
              {parkConsumptionWithArea.length} park için tüketim verileri ({parkConsumptionWithArea.filter(p => p.area > 0).length} park için alan bilgisi mevcut)
            </p>
            <div ref={scatterChartRef}>
            <ResponsiveContainer width="100%" height={500}>
              <ScatterChart margin={{ top: 20, right: 30, bottom: 50, left: 60 }}>
                <CartesianGrid />
                <XAxis 
                  type="number" 
                  dataKey="area" 
                  name="Park Alanı (m²)" 
                  label={{ value: 'Park Alanı (m²)', position: 'insideBottom', offset: -5, style: { fontSize: '12px' } }}
                  domain={['dataMin', 'dataMax']}
                  tick={{ fontSize: 10 }}
                />
                <YAxis 
                  type="number" 
                  dataKey="consumption" 
                  name="Su Tüketimi (m³)" 
                  label={{ value: 'Su Tüketimi (m³)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: '12px' } }}
                  domain={['dataMin', 'dataMax']}
                  tick={{ fontSize: 10 }}
                  width={55}
                />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className={styles.tooltip}>
                          <p><strong>{data.parkName || `Park ${data.parkId}`}</strong></p>
                          <p>Alan: {data.area > 0 ? data.area.toFixed(2) + ' m²' : 'Bilinmiyor'}</p>
                          <p>Tüketim: {data.consumption.toFixed(2)} m³</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Scatter name="Parklar" data={parkConsumptionWithArea} fill="#3b82f6" />
              </ScatterChart>
            </ResponsiveContainer>
            </div>
            <div style={{ marginTop: '20px', textAlign: 'center', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => generateChartPDF(
                  scatterChartRef,
                  'Park Boyutu vs Su Tüketimi',
                  `Park_Boyutu_vs_Tuketim_${selectedRegion}_${new Date().toISOString().split('T')[0]}.pdf`
                )}
                disabled={generating}
                className={styles.generatePdfButton}
              >
                {generating ? 'PDF Oluşturuluyor...' : 'Grafik PDF Indir'}
              </button>
                {emailConfigEnabled && (
                  <button
                    onClick={() => {
                      setEmailSubject('Park Boyutu vs Su Tüketimi Grafiği');
                      setEmailChartType('scatter');
                      setShowEmailModal(true);
                    }}
                    disabled={generating}
                    className={styles.generatePdfButton}
                    style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                  >
                    E-posta ile Gönder
                  </button>
                )}
            </div>
          </div>
        )}

        {selectedRegion && parkConsumption.length > 0 && activeTab === 'timeseries' && (
          <div className={styles.chartContainer}>
            <h3>Park Zaman Serisi Analizi</h3>
            
            {/* Park Arama */}
            <div className={styles.reportSelectorRow} style={{ marginBottom: '20px' }}>
              <label>Park Ara:</label>
              <input
                type="text"
                placeholder="Park adı veya ID ile ara..."
                value={timeseriesSearchTerm}
                onChange={(e) => setTimeseriesSearchTerm(e.target.value)}
                className={styles.searchInput}
                style={{ maxWidth: '300px' }}
              />
            </div>
            
            {/* Filtrelenmiş park listesi */}
            {(() => {
              const filteredParks = parkConsumption.filter(park => {
                if (!timeseriesSearchTerm.trim()) return true;
                const searchLower = timeseriesSearchTerm.toLowerCase();
                const parkName = (park.parkName || `Park ${park.parkId}`).toLowerCase();
                return parkName.includes(searchLower) || String(park.parkId).includes(timeseriesSearchTerm);
              });
              
              return (
                <div className={styles.reportSelectorRow} style={{ marginBottom: '20px' }}>
                  <label>Park Seçin ({filteredParks.length} park):</label>
                  <select
                    value={selectedParkForChart || ''}
                    onChange={(e) => setSelectedParkForChart(e.target.value ? parseInt(e.target.value) : null)}
                    className={styles.parkSelect}
                    style={{ maxWidth: '300px' }}
                  >
                    <option value="">-- Park Seçin --</option>
                    {filteredParks.map(park => (
                      <option key={park.parkId} value={park.parkId}>
                        {park.parkName || `Park ${park.parkId}`}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })()}
            
            {selectedParkForChart && timeSeriesData.length > 0 && (
              <>
                <div ref={timeSeriesChartRef}>
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart margin={{ top: 20, right: 30, left: 60, bottom: 100 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="period" 
                      angle={-60}
                      textAnchor="end"
                      height={120}
                      tick={{ fontSize: 9 }}
                      interval="preserveStartEnd"
                      dx={-5}
                      dy={5}
                      tickFormatter={(value, index) => {
                        // Sadece yılları göster (her yılın ilk verisinde)
                        const data = timeSeriesData[index];
                        if (data && data.year) {
                          // Aynı yılın önceki verisi varsa boş döndür
                          if (index > 0 && timeSeriesData[index - 1]?.year === data.year) {
                            return '';
                          }
                          return String(data.year);
                        }
                        // Tarih çok uzunsa kısalt
                        if (value && value.length > 15) {
                          return value.substring(0, 12) + '...';
                        }
                        return value;
                      }}
                    />
                    <YAxis 
                      label={{ value: 'Su Tüketimi (m³)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: '12px' } }}
                      tick={{ fontSize: 10 }}
                      width={55}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className={styles.tooltip}>
                              <p><strong>{data.period}</strong></p>
                              <p>Tüketim: {data.consumption.toFixed(2)} m³</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Legend />
                    {/* Yaz ayları için kırmızı çizgi (Nisan-Kasım: 4-10) */}
                    <Line 
                      type="monotone" 
                      dataKey="consumption" 
                      data={timeSeriesData.filter(d => d.month && d.month >= 4 && d.month <= 10)}
                      stroke="#ef4444"
                      strokeWidth={3} 
                      name="Yaz Ayları (Nisan-Kasım)"
                      dot={{ r: 5, fill: '#ef4444' }}
                      activeDot={{ r: 7 }}
                      connectNulls={true}
                    />
                    {/* Kış ayları için mavi çizgi (Aralık-Mart: 12, 1, 2, 3) */}
                    <Line 
                      type="monotone" 
                      dataKey="consumption" 
                      data={timeSeriesData.filter(d => d.month && (d.month < 4 || d.month > 10))}
                      stroke="#3b82f6"
                      strokeWidth={3} 
                      name="Kış Ayları (Aralık-Mart)"
                      dot={{ r: 5, fill: '#3b82f6' }}
                      activeDot={{ r: 7 }}
                      connectNulls={true}
                    />
                  </LineChart>
                </ResponsiveContainer>
                
                {/* Lejant */}
                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '30px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '3px', backgroundColor: '#ef4444' }}></div>
                    <span>Yaz Ayları (Nisan-Kasım)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '3px', backgroundColor: '#3b82f6' }}></div>
                    <span>Kış Ayları (Aralık-Mart)</span>
                  </div>
                </div>
                </div>
                <div style={{ marginTop: '20px', textAlign: 'center', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                  <button
                    onClick={() => {
                      const selectedPark = parkConsumption.find(p => p.parkId === selectedParkForChart);
                      const parkName = selectedPark?.parkName || `Park ${selectedParkForChart}`;
                      generateChartPDF(
                        timeSeriesChartRef,
                        `Zaman Serisi Analizi: ${parkName}`,
                        `Zaman_Serisi_${parkName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30)}_${new Date().toISOString().split('T')[0]}.pdf`
                      );
                    }}
                    disabled={generating}
                    className={styles.generatePdfButton}
                  >
                    {generating ? 'PDF Oluşturuluyor...' : 'Grafik PDF Indir'}
                  </button>
                  {emailConfigEnabled && (
                    <button
                      onClick={() => {
                        const selectedPark = parkConsumption.find(p => p.parkId === selectedParkForChart);
                        const parkName = selectedPark?.parkName || `Park ${selectedParkForChart}`;
                        setEmailSubject(`Zaman Serisi Analizi: ${parkName}`);
                        setEmailChartType('timeseries');
                        setShowEmailModal(true);
                      }}
                      disabled={generating}
                      className={styles.generatePdfButton}
                      style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                    >
                      E-posta ile Gönder
                    </button>
                  )}
                </div>
              </>
            )}
            
            {selectedParkForChart && timeSeriesData.length === 0 && !loading && (
              <div className={styles.emptyStateContainer}>
                <p>Bu park için zaman serisi verisi bulunamadı.</p>
              </div>
            )}
          </div>
        )}

        {selectedRegion && parkConsumptionWithArea.length > 0 && activeTab === 'area' && (
          <div className={styles.chartContainer}>
            <h3>Yeşil Alan vs Su Tüketimi Analizi</h3>
            <p className={styles.dataInfo}>
              {parkConsumptionWithArea.filter(p => p.area > 0).length} park için yeşil alan bilgisi mevcut
            </p>
            
            {/* Alan vs Tüketim Scatter Chart */}
            <div style={{ marginBottom: '40px' }} ref={areaScatterChartRef}>
              <h4 style={{ marginBottom: '15px', color: '#1e293b' }}>Park Alanı vs Su Tüketimi</h4>
              <ResponsiveContainer width="100%" height={500}>
                <ScatterChart margin={{ top: 20, right: 30, bottom: 50, left: 60 }}>
                  <CartesianGrid />
                  <XAxis 
                    type="number" 
                    dataKey="area" 
                    name="Yeşil Alan (m²)" 
                    label={{ value: 'Yeşil Alan (m²)', position: 'insideBottom', offset: -5, style: { fontSize: '12px' } }}
                    domain={['dataMin', 'dataMax']}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis 
                    type="number" 
                    dataKey="consumption" 
                    name="Su Tüketimi (m³)" 
                    label={{ value: 'Su Tüketimi (m³)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: '12px' } }}
                    domain={['dataMin', 'dataMax']}
                    tick={{ fontSize: 10 }}
                    width={55}
                  />
                  <Tooltip 
                    cursor={{ strokeDasharray: '3 3' }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        const ratio = data.area > 0 ? (data.consumption / data.area).toFixed(4) : '--';
                        return (
                          <div className={styles.tooltip}>
                            <p><strong>{data.parkName || `Park ${data.parkId}`}</strong></p>
                            <p>Yeşil Alan: {data.area > 0 ? data.area.toFixed(2) + ' m²' : 'Bilinmiyor'}</p>
                            <p>Tüketim: {data.consumption.toFixed(2)} m³</p>
                            <p>Tüketim/Alan: {ratio} m³/m²</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Scatter 
                    name="Parklar" 
                    data={parkConsumptionWithArea.filter(p => p.area > 0)} 
                    fill="#10b981" 
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>

            {/* Tüketim/Alan Oranı Bar Chart */}
            <div style={{ marginBottom: '40px' }} ref={areaBarChartRef}>
              <h4 style={{ marginBottom: '15px', color: '#1e293b' }}>Tüketim/Alan Oranı (m³/m²) - En Yüksek 20 Park</h4>
              <ResponsiveContainer width="100%" height={500}>
                <BarChart 
                  margin={{ top: 20, right: 30, bottom: 120, left: 50 }}
                  data={parkConsumptionWithArea
                    .filter(p => p.area > 0)
                    .map(p => ({
                      ...p,
                      ratio: p.consumption / p.area
                    }))
                    .sort((a, b) => b.ratio - a.ratio)
                    .slice(0, 20)
                    .map((p, idx) => {
                      // Park ismini kısalt - PDF için optimize
                      const fullName = p.parkName || `Park ${p.parkId}`;
                      let shortName = fullName;
                      if (fullName.length > 25) {
                        shortName = fullName.substring(0, 22) + '...';
                      }
                      return {
                        ...p,
                        parkNameShort: shortName,
                        parkNameFull: fullName, // Tooltip için tam isim
                        ratio: p.consumption / p.area
                      };
                    })
                  }
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="parkNameShort" 
                    angle={-60}
                    textAnchor="end"
                    height={140}
                    tick={{ fontSize: 9 }}
                    interval={0}
                    dx={-5}
                    dy={5}
                  />
                  <YAxis 
                    label={{ value: 'Tüketim/Alan Oranı (m³/m²)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                    tick={{ fontSize: 10 }}
                    width={60}
                  />
                  <Tooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className={styles.tooltip}>
                            <p><strong>{data.parkNameFull || data.parkName || `Park ${data.parkId}`}</strong></p>
                            <p>Yeşil Alan: {data.area.toFixed(2)} m²</p>
                            <p>Tüketim: {data.consumption.toFixed(2)} m³</p>
                            <p>Oran: {data.ratio.toFixed(4)} m³/m²</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar 
                    dataKey="ratio" 
                    fill="#3b82f6"
                    name="Tüketim/Alan Oranı"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* PDF İndir Butonları */}
            <div style={{ marginTop: '30px', marginBottom: '40px', display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => generateChartPDF(
                  areaScatterChartRef,
                  'Park Alanı vs Su Tüketimi',
                  `Park_Alani_vs_Tuketim_${selectedRegion}_${new Date().toISOString().split('T')[0]}.pdf`
                )}
                disabled={generating}
                className={styles.generatePdfButton}
              >
                {generating ? 'PDF Oluşturuluyor...' : 'Alan vs Tuketim PDF Indir'}
              </button>
              {emailConfigEnabled && (
                <button
                  onClick={() => {
                    setEmailSubject('Park Alanı vs Su Tüketimi Grafiği');
                    setEmailChartType('area-scatter');
                    setShowEmailModal(true);
                  }}
                  disabled={generating}
                  className={styles.generatePdfButton}
                  style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                >
                  E-posta ile Gönder
                </button>
              )}
              <button
                onClick={() => generateChartPDF(
                  areaBarChartRef,
                  'Tüketim/Alan Oranı - En Yüksek 20 Park',
                  `Tuketim_Alan_Orani_${selectedRegion}_${new Date().toISOString().split('T')[0]}.pdf`
                )}
                disabled={generating}
                className={styles.generatePdfButton}
              >
                {generating ? 'PDF Oluşturuluyor...' : 'Tuketim/Alan Orani PDF Indir'}
              </button>
              {emailConfigEnabled && (
                <button
                  onClick={() => {
                    setEmailSubject('Tüketim/Alan Oranı - En Yüksek 20 Park');
                    setEmailChartType('area-bar');
                    setShowEmailModal(true);
                  }}
                  disabled={generating}
                  className={styles.generatePdfButton}
                  style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                >
                  E-posta ile Gönder
                </button>
              )}
            </div>

            {/* Alan Dağılımı */}
            <div>
              <h4 style={{ marginBottom: '15px', color: '#1e293b' }}>Yeşil Alan Dağılımı</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '20px' }}>
                <div style={{ padding: '15px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2563eb' }}>
                    {parkConsumptionWithArea.filter(p => p.area > 0).length}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    Park Alan Bilgisi Mevcut
                  </div>
                </div>
                <div style={{ padding: '15px', background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#059669' }}>
                    {parkConsumptionWithArea.filter(p => p.area > 0).reduce((sum, p) => sum + p.area, 0).toFixed(0)} m²
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    Toplam Yeşil Alan
                  </div>
                </div>
                <div style={{ padding: '15px', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderRadius: '8px', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d97706' }}>
                    {parkConsumptionWithArea.filter(p => p.area > 0).length > 0 
                      ? (parkConsumptionWithArea.filter(p => p.area > 0).reduce((sum, p) => sum + p.area, 0) / parkConsumptionWithArea.filter(p => p.area > 0).length).toFixed(0)
                      : '0'} m²
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    Ortalama Park Alanı
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedRegion && !loading && parkConsumption.length === 0 && (
          <div className={styles.emptyStateContainer}>
            <p>Bu bölge için su tüketim verisi bulunamadı.</p>
          </div>
        )}
      </div>

      {/* E-posta Gönderme Modal */}
      {showEmailModal && (
        <div className={styles.addUserModal}>
          <div className={styles.modalContent} style={{ maxWidth: '600px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '2px solid rgba(59, 130, 246, 0.1)'
            }}>
              <h3 style={{ margin: 0, color: '#1e293b', fontSize: '22px', fontWeight: '700' }}>
                E-posta ile Gönder
              </h3>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailTo('');
                  setEmailSubject('');
                  setEmailBody('');
                  setEmailChartType(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#64748b',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            <div className={styles.formGroup}>
              <label>Alıcı E-posta Adresi(leri):</label>
              <input
                type="email"
                placeholder="ornek@email.com (virgülle ayırarak birden fazla ekleyebilirsiniz)"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                required
                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              />
              <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                Birden fazla e-posta için virgülle ayırın: email1@example.com, email2@example.com
              </small>
            </div>

            <div className={styles.formGroup}>
              <label>Konu:</label>
              <input
                type="text"
                placeholder="E-posta konusu"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              />
              {!emailSubject && (
                <small style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Boş bırakılırsa otomatik konu kullanılacak
                </small>
              )}
            </div>

            <div className={styles.formGroup}>
              <label>Mesaj:</label>
              <textarea
                placeholder="E-posta mesajı (opsiyonel)"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows={4}
                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>

            <div style={{ 
              padding: '12px', 
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', 
              borderRadius: '8px', 
              border: '1px solid rgba(59, 130, 246, 0.2)',
              marginBottom: '20px'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#1e293b' }}>
                <strong>Ek:</strong> PDF raporu otomatik olarak eklenecektir.
              </p>
            </div>

            <div className={styles.modalActions} style={{ 
              paddingTop: '20px',
              borderTop: '2px solid rgba(59, 130, 246, 0.1)'
            }}>
              <button 
                type="button" 
                onClick={async () => {
                  // Hangi tip e-posta gönderileceğini kontrol et
                  if (emailChartType && emailChartType !== 'report') {
                    // Grafik e-postası gönder
                    let chartRef = null;
                    let chartTitle = emailSubject;
                    let fileName = '';
                    
                    if (emailChartType === 'scatter') {
                      chartRef = scatterChartRef;
                      fileName = `Park_Boyutu_vs_Tuketim_${selectedRegion}_${new Date().toISOString().split('T')[0]}.pdf`;
                    } else if (emailChartType === 'timeseries') {
                      chartRef = timeSeriesChartRef;
                      const selectedPark = parkConsumption.find(p => p.parkId === selectedParkForChart);
                      const parkName = selectedPark?.parkName || `Park ${selectedParkForChart}`;
                      fileName = `Zaman_Serisi_${parkName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30)}_${new Date().toISOString().split('T')[0]}.pdf`;
                    } else if (emailChartType === 'area-scatter') {
                      chartRef = areaScatterChartRef;
                      fileName = `Park_Alani_vs_Tuketim_${selectedRegion}_${new Date().toISOString().split('T')[0]}.pdf`;
                    } else if (emailChartType === 'area-bar') {
                      chartRef = areaBarChartRef;
                      fileName = `Tuketim_Alan_Orani_${selectedRegion}_${new Date().toISOString().split('T')[0]}.pdf`;
                    }
                    
                    if (chartRef) {
                      await sendChartEmail(chartRef, chartTitle, fileName);
                    } else {
                      await sendEmailWithPDF();
                    }
                  } else {
                    // Normal PDF e-postası gönder
                    await sendEmailWithPDF();
                  }
                }}
                disabled={sendingEmail || !emailTo} 
                className={styles.submitButton}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
              >
                {sendingEmail ? 'Gonderiliyor...' : 'E-posta Gonder'}
              </button>
              <button 
                type="button" 
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailTo('');
                  setEmailSubject('');
                  setEmailBody('');
                  setEmailChartType(null);
                }}
                className={styles.cancelButton}
                disabled={sendingEmail}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast Notification */}
      {showSuccessToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 10000,
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          padding: '20px 24px',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
          minWidth: '350px',
          maxWidth: '500px',
          animation: 'slideInRight 0.3s ease-out',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginTop: '2px'
            }}>
              <span style={{ fontSize: '16px' }}></span>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '8px',
                lineHeight: '1.4'
              }}>
                Başarılı!
              </div>
              <div style={{
                fontSize: '14px',
                lineHeight: '1.6',
                opacity: 0.95,
                whiteSpace: 'pre-line'
              }}>
                {successMessage}
              </div>
            </div>
            <button
              onClick={() => setShowSuccessToast(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.8,
                flexShrink: 0
              }}
              onMouseOver={(e) => e.target.style.opacity = '1'}
              onMouseOut={(e) => e.target.style.opacity = '0.8'}
            >
              ×
            </button>
          </div>
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default WaterConsumptionReport;
